import {
  ENGINE_APPLIED,
  ENGINE_DECOMMISSIONING,
  ENGINE_RECOMMISSIONING,
  PHASE_DECOMMISSION_IN_FLIGHT,
  PHASE_NEEDS_A_HUMAN,
  PHASE_NOT_APPLIED,
  PHASE_REPLACEMENT_LIVE_SOURCE_LIVE,
  PHASE_SOURCE_DEACTIVATED,
  type EngineMigrationPhase,
  type EngineTaskReport,
} from "@/services/migrations/engine/types";
import type { CoverageTone } from "./coverage";

/**
 * The decommission phase, as a screen has to reason about it.
 *
 * WHY THIS PHASE EXISTS, stated once so nothing below has to re-argue it. `apply` creates
 * the replacement and stops, and everything it refuses to do is correct: it deploys a Flow
 * as `Draft`, it never activates it, and it will not touch the source. But a customer whose
 * WorkflowRule is still firing and whose Flow has been activated has TWO automations on one
 * object. They get two field updates, two emails, two records. That is not a subtle
 * degradation a report catches later — it is the migration actively making things worse
 * than not migrating at all.
 *
 * AND IT IS THE MOST DANGEROUS ACT IN THE ENGINE, dangerous in a way `apply` is not:
 *
 *   an apply gone wrong          leaves an unused Draft Flow. Additive, probed absent
 *                                first, deletable by an undo that can prove it made it.
 *   a decommission gone wrong    STOPS A BUSINESS PROCESS. Nothing is created that can be
 *                                deleted; something the customer relies on quietly stops
 *                                happening, and the records that pass through while it is
 *                                off are never replayed.
 *
 * So the rules here run the same way the server's do: THE FORWARD PATH FAILS CLOSED AND THE
 * REVERSE PATH FAILS OPEN. Any doubt at all blocks the deactivation; the same doubt does not
 * block a reactivation, because refusing to turn a customer's automation back on is the one
 * outcome worse than turning on something that was already on.
 *
 * WHAT THIS MODULE IS NOT. It is not the gate. `decommission.py` re-checks eleven
 * preconditions on every request — the flag, the op, the literal `check_only`, the token,
 * a recorded GATE_DECOMMISSION approval, the apply having settled `APPLIED` exactly, the
 * source digest still matching what was compiled, and (asked of the org, because no local
 * row can answer it) the replacement Flow being ACTIVE. Nothing here can weaken any of
 * them, and a client-side gate that believed it could would be the most dangerous code in
 * this app. This exists so that a refusal a screen could have predicted is one that never
 * gets sent, and so the two facts a human must be told before they click — the double-fire
 * risk, and that activating the Flow is a manual act nobody automates — are on the screen
 * rather than in a round trip.
 */

// ── the phase, as something a screen can print ──────────────────────────────────

export interface PhasePresentation {
  phase: EngineMigrationPhase;
  label: string;
  tone: CoverageTone;
  /** `true` while the migration is in the state both automations can be live in. */
  doubleFireRisk: boolean;
}

const PHASE_LABELS: Record<Exclude<EngineMigrationPhase, "">, string> = {
  [PHASE_NOT_APPLIED]: "not applied",
  [PHASE_REPLACEMENT_LIVE_SOURCE_LIVE]: "half done — source still live",
  [PHASE_DECOMMISSION_IN_FLIGHT]: "deploy in flight",
  [PHASE_SOURCE_DEACTIVATED]: "source deactivated",
  [PHASE_NEEDS_A_HUMAN]: "needs a human",
};

/**
 * One phase, with the tone it is allowed to wear.
 *
 * `source_deactivated` is the ONLY phase that may be green: it is the only one in which
 * exactly one automation is running on the object and it is the new one. The intermediate
 * phase is warm and never neutral, because "applied" reads like a finished job and the
 * whole hazard of this phase is that it is not one.
 *
 * An UNRECOGNISED or absent phase is neutral and labelled as unreported. It is not folded
 * into `not_applied`, which would be this client asserting the org's state off a string it
 * could not read.
 */
export function phasePresentation(
  phase: EngineMigrationPhase,
  doubleFireRisk: boolean
): PhasePresentation {
  if (phase === PHASE_SOURCE_DEACTIVATED) {
    return { phase, label: PHASE_LABELS[phase], tone: "success", doubleFireRisk: false };
  }
  if (phase === PHASE_REPLACEMENT_LIVE_SOURCE_LIVE) {
    return { phase, label: PHASE_LABELS[phase], tone: "warm", doubleFireRisk };
  }
  if (phase === PHASE_NEEDS_A_HUMAN) {
    return { phase, label: PHASE_LABELS[phase], tone: "destructive", doubleFireRisk };
  }
  if (phase === PHASE_DECOMMISSION_IN_FLIGHT || phase === PHASE_NOT_APPLIED) {
    return { phase, label: PHASE_LABELS[phase], tone: "neutral", doubleFireRisk };
  }
  return { phase: "", label: "phase not reported", tone: "neutral", doubleFireRisk };
}

// ── the confirmation tokens ─────────────────────────────────────────────────────

/**
 * The `REACTIVATE …` token, DERIVED — and labelled as derived wherever it is shown.
 *
 * The report renders `decommission.confirmation_token` for the DEACTIVATE direction only;
 * no recommission token is issued anywhere on the wire. This mirrors the server's
 * `required_recommission_confirmation` exactly, on the same three inputs, in the same
 * order — and the same precedent `derivedClearApplyToken` already sets. If the derivation
 * is ever wrong the server refuses with `CONFIRMATION_MISMATCH` and returns the token it
 * wanted, and that is then what the screen shows: nothing here can authorise a write the
 * server would not have authorised anyway.
 *
 * Empty on any missing input, and an empty token can never equal a typed one, so a screen
 * that could not derive it offers nothing rather than offering something wrong.
 */
export function derivedRecommissionToken(
  orgId: string,
  sourceFullName: string,
  compileKey: string
): string {
  if (!orgId || !sourceFullName || !compileKey) return "";
  return `REACTIVATE WorkflowRule ${sourceFullName} IN ${orgId} ${compileKey}`;
}

/** The source a decommission of this task would switch off, or `""`. */
export function decommissionSource(task: EngineTaskReport): string {
  return task.decommission.will_deactivate[0]?.full_name ?? "";
}

// ── the forward path: why it may not be offered ─────────────────────────────────

export type DecommissionBlockerCode =
  /** This family has no source automation this phase can switch off. */
  | "NOT_APPLICABLE"
  /** `PLAYGROUND_MIGRATION_DECOMMISSION` is off — the routes are not even mounted. */
  | "DECOMMISSION_DISABLED"
  /** No replacement has been applied, so there is nothing to switch the source over to. */
  | "NOTHING_APPLIED"
  /** A deploy that changes the source's active flag is already in flight or parked. */
  | "IN_FLIGHT"
  /** The source is already off. Nothing to do. */
  | "ALREADY_DECOMMISSIONED"
  /** The task is blocked; what the org contains is exactly what is not established. */
  | "NEEDS_A_HUMAN"
  /** No recorded GATE_DECOMMISSION approval names this task. */
  | "NOT_APPROVED"
  /** The approval names a different compile key than this artifact. */
  | "APPROVAL_STALE"
  /** The engine issued no token, so there is nothing to repeat. */
  | "NO_TOKEN"
  /** The typed sentence is not the one the gate will recompute. */
  | "TOKEN_MISMATCH";

export interface DecommissionBlocker {
  code: DecommissionBlockerCode;
  message: string;
}

/**
 * Why this task's source may not be deactivated right now, said BEFORE the request.
 *
 * A MIRROR, AND DELIBERATELY ONLY A MIRROR. Four of the server's preconditions cannot be
 * checked here at all because they are questions asked OF THE ORG at request time — that
 * the source rule still matches the digest it was compiled from, that it is still active,
 * that its read came back complete, and above all THAT THE REPLACEMENT FLOW IS ACTIVE IN
 * THE ORG. No local row can answer the last one, because this engine deploys Flows as
 * Draft and never activates them, so "active" is always something a human did in Setup.
 * Those arrive as refusals, and `decommissionAdvice` turns the ones a person can act on
 * into a next step rather than a wall.
 */
export function decommissionBlockers(
  task: EngineTaskReport,
  typedToken: string
): DecommissionBlocker[] {
  const dc = task.decommission;
  const blockers: DecommissionBlocker[] = [];

  if (!dc.applicable) {
    blockers.push({
      code: "NOT_APPLICABLE",
      message:
        "This migration has no source automation that this phase can switch off. Only a WorkflowRule is deactivated by redeploying it inactive; a Profile is not automation, and a Process Builder process is deactivated through its FlowDefinition, which this engine does not operate.",
    });
    // Nothing below is a meaningful sentence about a task with no source to deactivate.
    return blockers;
  }
  if (!dc.enabled) {
    blockers.push({
      code: "DECOMMISSION_DISABLED",
      message:
        "The decommission phase is off in this environment. It is a separate flag from the write path, and apply ON with this OFF is the intended configuration while a replacement is being watched — deploy it, let a human activate it, watch it run beside the rule it replaces, and only then switch the rule off.",
    });
  }
  if (dc.phase === PHASE_NOT_APPLIED) {
    blockers.push({
      code: "NOTHING_APPLIED",
      message:
        "No replacement has been applied to this org, so the source automation is untouched and running. Deactivating it now would leave the object with no automation at all — not migrated, gone.",
    });
  }
  if (
    dc.phase === PHASE_DECOMMISSION_IN_FLIGHT ||
    task.status === ENGINE_DECOMMISSIONING ||
    task.status === ENGINE_RECOMMISSIONING
  ) {
    blockers.push({
      code: "IN_FLIGHT",
      message:
        "A deploy that changes this rule's active flag is in flight or parked. Until it settles, whether this automation is running is not established — and sending another is the one ordering whose result neither side chose.",
    });
  }
  if (dc.phase === PHASE_SOURCE_DEACTIVATED) {
    blockers.push({
      code: "ALREADY_DECOMMISSIONED",
      message:
        "This source is already deactivated and the org confirmed it. Deploying the deactivation again would be a write with nothing to change; if you believe the rule is live, reactivate it explicitly rather than re-sending this.",
    });
  }
  if (dc.phase === PHASE_NEEDS_A_HUMAN) {
    blockers.push({
      code: "NEEDS_A_HUMAN",
      message:
        "This task is blocked, and whether the source automation is running is exactly what is not established here. Read the attempt history below before changing anything else in the org.",
    });
  }
  if (!dc.approved) {
    blockers.push({
      code: "NOT_APPROVED",
      message:
        "No recorded GATE_DECOMMISSION approval names this task. It is its own ledger and its own decision — an approval to CREATE the replacement is not an approval to stop the thing it replaces, and the second decision is taken later, on evidence the first did not have.",
    });
  } else if (!dc.approval_matches_artifact) {
    blockers.push({
      code: "APPROVAL_STALE",
      message:
        "The recorded decommission approval names a different compile key than this task's. The artifact recompiled after it was approved, so the approval does not transfer — re-approve the current one.",
    });
  }
  if (!dc.confirmation_token) {
    blockers.push({
      code: "NO_TOKEN",
      message: "The engine issued no confirmation token for this decommission.",
    });
  } else if (typedToken !== dc.confirmation_token) {
    blockers.push({
      code: "TOKEN_MISMATCH",
      message:
        "Type the confirmation sentence exactly. It names the SOURCE — the customer's own WorkflowRule — and not the artifact this engine compiled, because the deploy behind this gate modifies their component and not ours.",
    });
  }
  return blockers;
}

// ── what the screen must say even when nothing is blocking ──────────────────────

export type DecommissionAdviceCode =
  /** BOTH automations may be firing right now. The reason this phase exists. */
  | "DOUBLE_FIRE_RISK"
  /** The engine will refuse unless the Flow is ACTIVE, and activating it is a human act. */
  | "ACTIVATE_THE_FLOW_FIRST"
  /** The interval the source was off is not recovered by turning it back on. */
  | "INTERVAL_NOT_REPLAYED";

export interface DecommissionAdvice {
  code: DecommissionAdviceCode;
  tone: CoverageTone;
  message: string;
}

/**
 * The two things a person deciding this must be told, and the one a person undoing it must.
 *
 * `ACTIVATE_THE_FLOW_FIRST` is here because of a specific failure mode: the server refuses
 * with `REPLACEMENT_NOT_ACTIVE` when the org has no ACTIVE version of the replacement Flow,
 * and a reader who has not been told why is left staring at a refusal for a Flow they can
 * plainly see was deployed. The engine deploys Draft and NEVER activates — activation is
 * the moment the customer's behaviour actually changes, and doing it in the same motion as
 * the deactivation would mean there was never a supervised window at all. So it is a manual
 * act in Setup, on purpose, and the screen says so before the refusal rather than after it.
 */
export function decommissionAdvice(task: EngineTaskReport): DecommissionAdvice[] {
  const dc = task.decommission;
  if (!dc.applicable) return [];
  const advice: DecommissionAdvice[] = [];

  if (dc.double_fire_risk) {
    advice.push({
      code: "DOUBLE_FIRE_RISK",
      tone: "destructive",
      message:
        "THE MIGRATION IS HALF DONE. The replacement exists in this org and the source WorkflowRule is STILL LIVE. If somebody has activated the Flow, both are firing right now and this object is getting doubled field updates, doubled emails and doubled records. Decommission the source to finish, or roll the replacement back.",
    });
  }
  if (dc.phase === PHASE_REPLACEMENT_LIVE_SOURCE_LIVE || task.status === ENGINE_APPLIED) {
    advice.push({
      code: "ACTIVATE_THE_FLOW_FIRST",
      tone: "warm",
      message:
        "This engine deploys Flows as Draft and never activates them — activation is a human act in Setup, and it is deliberately not automated so that the replacement is watched running before anything is switched off. The decommission will be REFUSED until the org has an ACTIVE version of the replacement Flow. Activate it, watch it run, then come back.",
    });
  }
  if (dc.phase === PHASE_SOURCE_DEACTIVATED) {
    advice.push({
      code: "INTERVAL_NOT_REPLAYED",
      tone: "warm",
      message:
        "Reactivating restores the rule exactly — deactivation destroys nothing, and its criteria, formula, actions and time triggers all stayed in the org. What it does NOT restore is the interval: records that changed while the rule was off did not fire it, and turning it back on does not go back for them.",
    });
  }
  return advice;
}

// ── the reverse path ────────────────────────────────────────────────────────────

export type RecommissionBlockerCode =
  | "NOT_APPLICABLE"
  | "DECOMMISSION_DISABLED"
  | "NOTHING_DECOMMISSIONED"
  | "IN_FLIGHT"
  | "NOT_APPROVED"
  | "NO_TOKEN"
  | "TOKEN_MISMATCH";

export interface RecommissionBlocker {
  code: RecommissionBlockerCode;
  message: string;
}

/**
 * Why the source may not be switched back on — and there are deliberately fewer of these.
 *
 * THIS PATH FAILS OPEN. Everything the forward path checks about the replacement is checked
 * because switching the source OFF in favour of a bad replacement is the damage. None of it
 * applies to switching the source back ON: a live source is the state the customer was in
 * before this engine touched anything, and refusing to restore it because some other fact
 * could not be established would be refusing the remedy for the reason the remedy is needed.
 *
 * So this asks only whether there is a decommission that may have taken effect, and whether
 * the gate factors that stop this being a way IN are present. Note in particular that a
 * BLOCKED task is not a blocker here: `blocked` is exactly where a decommission that
 * mismatched, that could not be verified, or that Salesforce accepted without effect leaves
 * a task, and those are precisely the cases where a human most needs to turn the automation
 * back on. A reverse path unreachable from the states its forward path produces is not a
 * reverse path.
 */
export function recommissionBlockers(
  task: EngineTaskReport,
  typedToken: string,
  derivedToken: string
): RecommissionBlocker[] {
  const dc = task.decommission;
  const blockers: RecommissionBlocker[] = [];

  if (!dc.applicable) {
    blockers.push({
      code: "NOT_APPLICABLE",
      message: "This migration has no source automation this engine switches off or on.",
    });
    return blockers;
  }
  if (!dc.enabled) {
    blockers.push({
      code: "DECOMMISSION_DISABLED",
      message:
        "The decommission phase is off in this environment, so neither direction of it is mounted here. A source that is off can still be reactivated by hand: Setup → Workflow Rules → the rule → Activate.",
    });
  }
  if (dc.phase !== PHASE_SOURCE_DEACTIVATED && dc.phase !== PHASE_NEEDS_A_HUMAN) {
    blockers.push({
      code: "NOTHING_DECOMMISSIONED",
      message:
        "This engine has never switched this source off, so there is nothing to switch back on.",
    });
  }
  if (
    dc.phase === PHASE_DECOMMISSION_IN_FLIGHT ||
    task.status === ENGINE_DECOMMISSIONING ||
    task.status === ENGINE_RECOMMISSIONING
  ) {
    blockers.push({
      code: "IN_FLIGHT",
      message:
        "A deactivation may still be in flight. Reactivating while it is running is the one ordering whose result neither side chose — resolve it first.",
    });
  }
  if (!dc.approved) {
    blockers.push({
      code: "NOT_APPROVED",
      message:
        "No recorded GATE_RECOMMISSION approval names this task. The reverse path still has a gate, because a reverse path with no gate is a way in.",
    });
  }
  if (!derivedToken) {
    blockers.push({
      code: "NO_TOKEN",
      message:
        "The REACTIVATE sentence could not be derived — the org, the source name or the compile key is missing from this report.",
    });
  } else if (typedToken !== derivedToken) {
    blockers.push({
      code: "TOKEN_MISMATCH",
      message:
        "Type the reactivation sentence exactly. It names the source rule, not the compiled Flow.",
    });
  }
  return blockers;
}

/**
 * The sentence a screen shows when a reactivation itself has failed.
 *
 * THE WORST STATE THIS SERVICE CAN PRODUCE: the customer's automation is off and we could
 * not turn it back on. The engine does not retry itself — it parks loudly and names the
 * manual act, which is the one thing a human can do without us. Repeating it here means a
 * reader is never left with only a red outcome code.
 */
export const RECOMMISSION_MANUAL_STEP =
  "A human can reactivate this rule directly and without this service: Setup → Workflow Rules → the rule → Activate.";
